import { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { captureCatch, reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import {
  createCashInflow,
  createCashOutflow,
  fetchRemoteOutflowsForSession,
  voidCashOutflow,
  type CashOutflowRow,
} from '../../infra/cashOutflowRepo';
import type {
  CashMovementCategory,
  CashMovementDirection,
} from '../../infra/cashOutflowCategories';
import { mapCashCommandError, rawCashErrorText } from '../../infra/mapCashCommandError';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import type { OperationalConfirmOptions } from '../../../shared/ui/useOperationalConfirm';
import { bumpCashSessionChangeSignal } from '../../infra/cashSessionChangeSignal';
import { invalidateCashSummaryCaches } from '../../infra/cashSummaryRemoteCache';
import { formatMoney } from '../workspace/cashFormatMoney';
import { requireBranchSupervisorPin, authorizeSupervisorPin } from '../../../shell/session/supervisorPinApi';

/** Valida el monto del egreso. Retorna null si es válido, o un mensaje de error. */
export function validateOutflowAmount(amountText: string): number | null {
  return parseDecimalInput(amountText, { min: 0.01, money: true });
}

/** Valida el motivo de anulación. Retorna true si es válido (>= 4 chars). */
export function isValidVoidReason(voidReason: string): boolean {
  return voidReason.trim().length >= 4;
}

/** Determina si se debe pedir confirmación por monto mayor al efectivo esperado. */
export function shouldConfirmOverExpected(
  amount: number,
  expectedCashInDrawer: number | null
): boolean {
  return expectedCashInDrawer != null && amount > expectedCashInDrawer;
}

type Params = {
  branchId: string | null;
  sessionId: string | null | undefined;
  organizationId?: string | null;
  enabled: boolean;
  canEdit: boolean;
  canVoid: boolean;
  isOwner?: boolean;
  userId: string | undefined;
  expectedCashInDrawer: number | null;
  onChanged: () => void | Promise<void>;
  confirm: (options: OperationalConfirmOptions) => Promise<boolean>;
  promptSupervisorPin?: (opts?: { title?: string; message?: string }) => Promise<string | null>;
};

export function useCashOutflows({
  branchId,
  sessionId,
  organizationId,
  enabled,
  canEdit,
  canVoid,
  isOwner = false,
  userId,
  expectedCashInDrawer,
  onChanged,
  confirm,
  promptSupervisorPin,
}: Params) {
  const [outflows, setOutflows] = useState<CashOutflowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerDirection, setRegisterDirection] = useState<CashMovementDirection>('out');
  const [submitting, setSubmitting] = useState(false);
  const [voidTarget, setVoidTarget] = useState<CashOutflowRow | null>(null);

  const reload = useCallback(async () => {
    if (!sessionId || !enabled) {
      setOutflows([]);
      setLoadError(false);
      return;
    }
    setLoading(true);
    try {
      // Incluye anulados: la lista muestra el movimiento tachado + "Anulado".
      const rows = await fetchRemoteOutflowsForSession(sessionId);
      if (rows == null) {
        // Error remoto: conservar el snapshot y marcar error para retry.
        setLoadError(true);
        return;
      }
      rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setOutflows(rows);
      setLoadError(false);
    } catch (e) {
      captureCatch('cash', 'useCashOutflows.reload')(e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openRegister = useCallback((direction: CashMovementDirection) => {
    setRegisterDirection(direction);
    setRegisterOpen(true);
  }, []);

  const runCreate = useCallback(
    async (input: {
      direction: CashMovementDirection;
      category: CashMovementCategory;
      amount: number;
      note?: string;
    }) => {
      if (!branchId || !sessionId) return;
      const isInflow = input.direction === 'in';
      setSubmitting(true);
      try {
        const create = isInflow ? createCashInflow : createCashOutflow;
        await create({
          branchId,
          cashSessionId: sessionId,
          category: input.category,
          amount: input.amount,
          note: input.note,
          createdBy: userId,
          organizationId: organizationId ?? undefined,
        });
        invalidateCashSummaryCaches(branchId);
        bumpCashSessionChangeSignal();
        await reload();
        await onChanged();
        setRegisterOpen(false);
        showSuccess(isInflow ? 'Ingreso registrado' : 'Egreso registrado');
      } catch (error: unknown) {
        const mapped = mapCashCommandError(rawCashErrorText(error));
        if (mapped.expected) {
          showError(
            isInflow ? 'No se pudo registrar el ingreso' : 'No se pudo registrar el egreso',
            mapped.userMessage
          );
        } else {
          presentUnexpectedFailure({
            module: 'cash',
            op: isInflow ? 'inflow.create' : 'outflow.create',
            error,
            userMessage: mapped.userMessage,
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
    [branchId, sessionId, organizationId, userId, reload, onChanged]
  );

  const handleRegister = useCallback(
    async (raw: { category: CashMovementCategory; amountText: string; note: string }) => {
      const isInflow = registerDirection === 'in';
      if (!canEdit) {
        showError(
          'Sin permisos',
          isInflow
            ? 'Tu perfil no puede registrar ingresos.'
            : 'Tu perfil no puede registrar egresos.'
        );
        return;
      }
      const amount = validateOutflowAmount(raw.amountText);
      if (amount == null) {
        showError('Monto inválido', 'Ingresá un monto mayor a cero.');
        return;
      }
      const proceed = () => {
        void runCreate({
          direction: registerDirection,
          category: raw.category,
          amount,
          note: raw.note.trim() || undefined,
        });
      };
      // El warning "supera el efectivo esperado" solo aplica a egresos.
      if (!isInflow && shouldConfirmOverExpected(amount, expectedCashInDrawer)) {
        const ok = await confirm({
          title: 'Monto mayor al efectivo esperado',
          message: `El egreso (${formatMoney(amount)}) supera el efectivo esperado en caja (${formatMoney(expectedCashInDrawer!)}). ¿Registrar igual?`,
          confirmLabel: 'Registrar igual',
        });
        if (!ok) return;
      }
      proceed();
    },
    [canEdit, confirm, expectedCashInDrawer, registerDirection, runCreate]
  );

  const runVoid = useCallback(
    async (outflowId: string, voidReason: string, supervisorOverrideId?: string, isInflow = false) => {
      setSubmitting(true);
      try {
        await voidCashOutflow({
          outflowId,
          voidReason,
          voidedBy: userId,
          supervisorOverrideId,
        });
        if (branchId) {
          invalidateCashSummaryCaches(branchId);
        }
        bumpCashSessionChangeSignal();
        await reload();
        await onChanged();
        showSuccess(isInflow ? 'Ingreso anulado' : 'Egreso anulado', 'Quedó anulado.');
      } catch (error: unknown) {
        const mapped = mapCashCommandError(rawCashErrorText(error));
        if (mapped.expected) {
          showError('No se pudo anular', mapped.userMessage);
        } else {
          reportUserVisibleFailure({
            module: 'cash',
            op: 'cash.outflow_void',
            error,
            title: isInflow ? 'No se pudo anular el ingreso.' : 'No se pudo anular el egreso.',
            detail: mapped.userMessage,
          });
          showError('No se pudo anular', mapped.userMessage);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [userId, reload, onChanged, branchId]
  );

  const handleVoidPress = useCallback(
    async (outflow: CashOutflowRow) => {
      if (canVoid || isOwner) {
        setVoidTarget(outflow);
        return;
      }
      const isInflow = outflow.direction === 'in';
      if (!promptSupervisorPin || !branchId) {
        showError(
          'Sin permisos',
          isInflow
            ? 'Solo un supervisor puede anular ingresos.'
            : 'Solo un supervisor puede anular egresos.'
        );
        return;
      }
      try {
        await requireBranchSupervisorPin(branchId);
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'cash', op: 'pin-no-configurado', error: error, title: 'PIN no configurado', userMessage: 'Configurá un PIN de supervisor en Reglas del POS.' });
        return;
      }
      const pin = await promptSupervisorPin({
        message: isInflow
          ? 'Anular ingreso requiere autorización. Ingresá el PIN de supervisor.'
          : 'Anular egreso requiere autorización. Ingresá el PIN de supervisor.',
      });
      if (!pin) return;
      try {
        const overrideId = await authorizeSupervisorPin({
          branchId,
          pin,
          module: 'Cash',
          action: 'void',
          context: { outflowId: outflow.id },
        });
        // Store override on the target via pending field — open reason modal next.
        setVoidTarget({ ...outflow, _supervisorOverrideId: overrideId } as CashOutflowRow & {
          _supervisorOverrideId?: string;
        });
      } catch (e: unknown) {
        presentUnexpectedFailure({ module: 'cash', op: 'pin', error: e, title: 'PIN', userMessage: 'No se pudo autorizar.' });
      }
    },
    [canVoid, isOwner, promptSupervisorPin, branchId]
  );

  const confirmVoid = useCallback(
    async (voidReason: string) => {
      if (!voidTarget) return;
      const reason = voidReason.trim();
      if (!isValidVoidReason(voidReason)) {
        showError('Motivo requerido', 'El motivo debe tener al menos 4 caracteres.');
        return;
      }
      const overrideId = (voidTarget as CashOutflowRow & { _supervisorOverrideId?: string })
        ._supervisorOverrideId;
      // BUG-04: esperar a que runVoid termine antes de cerrar el modal.
      // Si falla, el usuario mantiene el contexto para reintentar.
      await runVoid(voidTarget.id, reason, overrideId, voidTarget.direction === 'in');
      setVoidTarget(null);
    },
    [voidTarget, runVoid]
  );

  return {
    outflows,
    loading,
    loadError,
    registerOpen,
    registerDirection,
    openRegister,
    setRegisterOpen,
    setRegisterDirection,
    submitting,
    handleRegister,
    handleVoidPress,
    voidTarget,
    setVoidTarget,
    confirmVoid,
    reload,
  };
}
